import { useEffect, useState } from "react";
import { LandingNavbar } from "../components/landing/LandingNavbar";
import { LandingPageFooter } from "../components/landing/LandingPageFooter";
import config from "../config";
import { toast } from "react-toastify";

const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,600;1,9..144,600&family=Outfit:wght@300;400;500&display=swap";

export default function ContactUsPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState("light");

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  // Inject Google Fonts on mount; clean up on unmount
  useEffect(() => {
    const existing = document.getElementById("hs-landing-fonts");
    if (existing) return;

    const link = document.createElement("link");
    link.id   = "hs-landing-fonts";
    link.rel  = "stylesheet";
    link.href = FONTS_HREF;
    document.head.appendChild(link);

    return () => {
      const el = document.getElementById("hs-landing-fonts");
      if (el) el.remove();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !phone.trim() || !message.trim()) {
      toast.error("All fields are required");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          message: message.trim(),
        }),
      });

      if (response.ok) {
        toast.success("Thank you! Your message has been sent successfully.");
        setName("");
        setEmail("");
        setPhone("");
        setMessage("");
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || "Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`hs-landing lp-flex lp-flex-col lp-min-h-screen ${theme === "light" ? "lp-light-theme" : ""}`}>
      <LandingNavbar theme={theme} toggleTheme={toggleTheme} />

      <main className="lp-flex-grow lp-pt-32 lp-pb-24 lp-px-6 lp-flex lp-items-center lp-justify-center">
        <div className="lp-max-lg lp-w-full lp-mx-auto lp-bg-surface lp-border lp-bc-b06 lp-r-3xl lp-p-10 md-p-16 lp-shadow-blue lp-relative lp-overflow-hidden">
          {/* Subtle background glow */}
          <div
            aria-hidden
            className="lp-absolute lp-top-0 lp-right-0 lp-w-80 lp-h-80 lp-r-full lp-op-15 lp-pointer-none lp-tx-30-neg30"
            style={{ background: "radial-gradient(circle, #4A9EFF 0%, transparent 70%)" }}
          />

          <div className="lp-relative lp-z-10">
            <h1
              className="lp-text-dark lp-mb-3 lp-text-center"
              style={{
                fontFamily: "'Fraunces', serif",
                fontWeight: 600,
                fontSize: "clamp(2rem, 3.5vw, 2.8rem)",
                lineHeight: 1.1,
              }}
            >
              Get in <span className="lp-text-blue" style={{ fontStyle: "italic" }}>touch</span>
            </h1>
            <p
              className="lp-text-muted lp-text-center lp-mb-8"
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
            >
              Have questions about Hubscroll or want to list your venue? Send us a message and we'll get back to you shortly.
            </p>

            <form onSubmit={handleSubmit} className="lp-flex lp-flex-col lp-gap-4">
              <div>
                <label className="lp-block lp-text-xs lp-uppercase lp-tracking-wide lp-text-muted lp-mb-2" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>
                  Full Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="Enter your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="lp-w-full lp-px-4 lp-py-3 lp-r-lg lp-border lp-bc-b15 lp-text-dark lp-bg-white lp-transition focus:lp-bc-blue"
                  style={{ outline: "none", fontFamily: "'Outfit', sans-serif" }}
                />
              </div>

              <div>
                <label className="lp-block lp-text-xs lp-uppercase lp-tracking-wide lp-text-muted lp-mb-2" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="lp-w-full lp-px-4 lp-py-3 lp-r-lg lp-border lp-bc-b15 lp-text-dark lp-bg-white lp-transition focus:lp-bc-blue"
                  style={{ outline: "none", fontFamily: "'Outfit', sans-serif" }}
                />
              </div>

              <div>
                <label className="lp-block lp-text-xs lp-uppercase lp-tracking-wide lp-text-muted lp-mb-2" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  required
                  placeholder="+971 50 123 4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="lp-w-full lp-px-4 lp-py-3 lp-r-lg lp-border lp-bc-b15 lp-text-dark lp-bg-white lp-transition focus:lp-bc-blue"
                  style={{ outline: "none", fontFamily: "'Outfit', sans-serif" }}
                />
              </div>

              <div>
                <label className="lp-block lp-text-xs lp-uppercase lp-tracking-wide lp-text-muted lp-mb-2" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>
                  Your Message
                </label>
                <textarea
                  required
                  rows="4"
                  placeholder="How can we help your business?"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="lp-w-full lp-px-4 lp-py-3 lp-r-lg lp-border lp-bc-b15 lp-text-dark lp-bg-white lp-transition focus:lp-bc-blue"
                  style={{ outline: "none", fontFamily: "'Outfit', sans-serif", resize: "none" }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="lp-mt-4 lp-w-full lp-py-35 lp-r-full lp-bg-blue lp-text-white lp-transition lp-hov-bg-blue-lt lp-font-medium lp-text-center"
                style={{ fontFamily: "'Outfit', sans-serif", border: "none", cursor: loading ? "not-allowed" : "pointer" }}
              >
                {loading ? "Sending Message..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </main>

      <LandingPageFooter />
    </div>
  );
}
